import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Bell, LifeBuoy, LogOut } from "lucide-react";
import { Brand } from "@/components/Brand";
import GlobalSearch from "@/components/GlobalSearch";
import { MobileTabBar, navFor } from "@/components/MobileTabBar";
import { Button } from "@/components/ui/button";
import { useAuth, homeFor } from "@/context/AuthContext";
import api from "@/lib/api";

export default function DashboardLayout({ children, title }) {
  const { user, extras, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => {
      setUnread(data.unread);
      setNotifs(data.notifications);
    }).catch(() => {});
  }, []);

  const items = [...navFor(user, extras), { to: "/support", label: "Support", icon: LifeBuoy }];

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      <aside className="w-64 shrink-0 navy-bg text-white hidden md:flex flex-col sticky top-0 h-screen">
        <div className="p-6 border-b border-white/10">
          <Link to="/"><Brand dark compact /></Link>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" aria-label="Dashboard">
          {items.map((item) => (
            <NavLink
              key={item.to + item.label}
              to={item.to}
              end={item.to.split("/").length <= 2}
              data-testid={`nav-${item.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[#2563EB] text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <p className="text-sm font-semibold truncate">{user?.name}</p>
          <p className="text-xs text-white/50 capitalize mb-3">{(user?.role || "").replace(/_/g, " ")}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => { await logout(); navigate("/"); }}
            className="text-white/70 hover:text-white hover:bg-white/10 w-full justify-start"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="bg-white border-b border-border/60 px-6 h-16 flex items-center justify-between gap-4 sticky top-0 z-40">
          <div className="flex items-center gap-4 flex-1">
            <Link to="/" className="md:hidden"><Brand compact /></Link>
            <h2 className="font-heading font-semibold text-[#0B1B3D] truncate text-sm sm:text-base">{title}</h2>
            <div className="hidden md:block flex-1 max-w-sm ml-4"><GlobalSearch /></div>
          </div>
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowNotifs((s) => !s)}
              className="relative"
              data-testid="notifications-btn"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5" />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#F5A623] text-[#0B1B3D] text-[10px] font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </Button>
            {showNotifs && (
              <div className="absolute right-0 mt-2 w-96 max-w-[90vw] bg-white border border-border rounded-xl shadow-xl z-50 overflow-hidden" data-testid="notifications-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <p className="font-heading font-semibold text-sm">Notifications</p>
                  <button
                    className="text-xs text-[#2563EB] font-medium"
                    data-testid="mark-all-read-btn"
                    onClick={async () => { await api.post("/notifications/read-all"); setUnread(0); }}
                  >
                    Mark all read
                  </button>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifs.length === 0 && <p className="text-sm text-muted-foreground p-4">No notifications yet.</p>}
                  {notifs.map((n) => (
                    <div key={n.id} className={`px-4 py-3 border-b border-border/50 ${!n.read ? "bg-[#2563EB]/5" : ""}`}>
                      <p className="text-sm font-medium">{n.title}</p>
                      {n.body && <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <main className="p-4 sm:p-6 lg:p-8 pb-28 md:pb-8 max-w-[1400px]">{children}</main>
      </div>

      <MobileTabBar />
    </div>
  );
}

export function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);
  if (loading)
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return null;
  if (roles && !roles.includes(user.role) && !["super_admin", "admin"].includes(user.role)) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="text-center">
          <p className="font-heading font-semibold text-lg">Access restricted</p>
          <p className="text-sm text-muted-foreground mt-2">Your role does not have access to this area.</p>
          <Button className="mt-4 rounded-full" onClick={() => navigate(homeFor(user))} data-testid="go-home-btn">
            Go to my dashboard
          </Button>
        </div>
      </div>
    );
  }
  return children;
}
